import React, { useState, useRef } from 'react';
import { useI18n } from '../../../context/I18nContext';
import { useUserSearch } from '../../../../hooks/useApi';
import { IconX, IconCheck } from '../../../components/icons';

/**
 * UserMultiPicker - Modal component for selecting multiple users
 * Uses react-query for data fetching
 * Styled to match ApproverFlowEditor's UserSearchSelector
 */
const UserMultiPicker = ({ 
    onSelect, 
    onClose, 
    excludeIds = [], 
    maxSelection = null,
    title = null 
}) => {
    const { t } = useI18n(['common', 'reminders', 'forms']);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const inputRef = useRef(null);

    // React Query hooks
    const { data: searchResult, isLoading: searching } = useUserSearch(
        searchQuery.length >= 2 ? searchQuery : null
    );

    // Process data
    const searchUsers = (searchResult?.data?.users || searchResult?.data || [])
        .filter(u => !excludeIds.includes(u.id));

    const isSelected = (userId) => {
        return selectedUsers.some(u => u.id === userId);
    };

    const toggleUser = (user) => {
        const userId = user.userId || user.id;
        const normalizedUser = {
            id: userId,
            displayName: user.userName || user.displayName,
            mail: user.userEmail || user.mail || user.userPrincipalName,
            department: user.department,
            jobTitle: user.jobTitle
        };

        if (isSelected(userId)) {
            setSelectedUsers(prev => prev.filter(u => u.id !== userId));
        } else {
            if (maxSelection && selectedUsers.length >= maxSelection) {
                return; // Don't add if max reached
            }
            setSelectedUsers(prev => [...prev, normalizedUser]);
        }
    };

    const handleConfirm = () => {
        onSelect(selectedUsers);
        onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    };

    const renderUserItem = (user) => {
        const userId = user.id || user.userId;
        const userName = user.displayName || user.userName;
        const selected = isSelected(userId);
        const disabled = !selected && maxSelection && selectedUsers.length >= maxSelection;

        return (
            <button
                key={userId}
                onClick={() => toggleUser(user)}
                disabled={disabled}
                className={`w-full flex items-center gap-2 p-2 text-left border-b last:border-b-0 ${
                    selected 
                        ? 'bg-blue-50' 
                        : disabled 
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-blue-50'
                }`}
            >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    selected ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                    {selected ? (
                        <IconCheck className="w-4 h-4" />
                    ) : (
                        userName?.[0]?.toUpperCase() || '?'
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{userName}</div>
                    <div className="text-xs text-gray-500 truncate">
                        {user.department && `${user.department} • `}
                        {user.jobTitle}
                    </div>
                </div>
            </button>
        );
    };

    return (
        <div 
            className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"
            onClick={onClose}
            onKeyDown={handleKeyDown}
        >
            <div 
                className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 flex flex-col"
                style={{ maxHeight: '70vh' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900">
                            {title || t('selectUsers', { ns: 'reminders' })}
                        </h3>
                        {selectedUsers.length > 0 && (
                            <p className="text-sm text-gray-500">
                                {t('selectedCount', { ns: 'reminders', count: selectedUsers.length })}
                                {maxSelection && ` / ${maxSelection}`}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    >
                        <IconX className="w-5 h-5" />
                    </button>
                </div>

                {/* Selected Users Pills */}
                {selectedUsers.length > 0 && (
                    <div className="px-4 py-2 border-b border-gray-200 flex flex-wrap gap-2">
                        {selectedUsers.map(user => (
                            <span
                                key={user.id}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                            >
                                {user.displayName}
                                <button
                                    onClick={() => toggleUser(user)}
                                    className="p-0.5 hover:bg-blue-200 rounded-full"
                                >
                                    <IconX className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* Search Input - Same style as ApproverFlowEditor */}
                <div className="p-4 border-b border-gray-200">
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('route.searchPlaceholder', { ns: 'forms' })}
                        className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                    />
                </div>

                {/* Content - Same style as ApproverFlowEditor */}
                <div className="flex-1 overflow-y-auto border rounded bg-white mx-4 mb-4" style={{ maxHeight: '300px' }}>
                    {searchQuery.length < 2 && (
                        <div className="p-3 text-center text-gray-500 text-sm">
                            {t('route.searchHint', { ns: 'forms' })}
                        </div>
                    )}

                    {searchQuery.length >= 2 && searching && (
                        <div className="p-3 text-center text-gray-500 text-sm">
                            {t('route.searching', { ns: 'forms' })}
                        </div>
                    )}

                    {searchQuery.length >= 2 && !searching && searchUsers.length > 0 && (
                        searchUsers.map(user => renderUserItem(user))
                    )}

                    {searchQuery.length >= 2 && !searching && searchUsers.length === 0 && (
                        <div className="p-3 text-center text-gray-500 text-sm">
                            {t('route.noResults', { ns: 'forms' })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                        {t('cancel', { ns: 'common' })}
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={selectedUsers.length === 0}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {t('confirm', { ns: 'common' })} ({selectedUsers.length})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserMultiPicker;
